/**
 * Formatting for the v2 apps. Everything here is tuned for a glance — from the driver's seat or
 * across a director's console — so it stays short, unambiguous, and never so precise that it takes
 * a second read.
 */

/**
 * m/s → mph. The API speaks metres per second throughout; these apps speak mph. One definition,
 * because drifting copies of this constant would mean different speed limits on screen.
 */
export const MPS_TO_MPH = 2.237;

/**
 * Uptime as the device reports it — seconds since boot, read from /proc/uptime.
 *
 * Mirrors formatUptime in ClientApps/shared/src/helpers, but renders "6d 14h" rather than
 * "6.6 Days" because these screens are about recognising "this has been up all shift" at a glance,
 * not about precision. (Kept out of the shared root barrel for exactly that name collision.)
 */
export function formatUptime(seconds: number | null | undefined): string {
    if (seconds == null || seconds <= 0) return "—";

    const total = Math.floor(seconds);
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

/**
 * How long ago something happened — typically the device's last check-in.
 *
 * Deliberately coarse past a minute. "seen 3 min ago" tells a driver what they need; a ticking
 * second count invites staring at the phone, which is the opposite of what these screens are for.
 */
export function formatAgo(value: Date | string | null | undefined, now = new Date()): string {
    if (!value) return "never";

    const then = new Date(value);
    if (Number.isNaN(then.getTime())) return "unknown";

    // The server sends UTC; a device clock that's slightly ahead shouldn't render "in 4 seconds".
    const seconds = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 1000));

    if (seconds < 45) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
}

/**
 * The browser's short time-zone name — "CDT", "GMT+1".
 *
 * Table headers carry this once ("Time (CDT)") rather than repeating a zone on every row, which is
 * the difference between a log a director can hand to a lawyer and one they have to caveat.
 */
export function timeZoneLabel(at: Date = new Date()): string {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(at);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
}

/** Date and time together, for logs where the day matters as much as the clock. */
export function formatDateTime(value: Date | string | undefined): string {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${d.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    })}`;
}

/** Clock time to the second — for windows where duration is the point. */
export function formatTimeOfDay(value: Date | string | undefined): string {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}

/** Local time, short. Directors read "14:31" and "Aug 5" faster than ISO anything. */
export function formatWhen(value: Date | string | undefined): string {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    const sameDay = new Date().toDateString() === d.toDateString();
    return sameDay
        ? d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })
        : d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
              " " +
              d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** Countdown for an active snooze, as m:ss. */
export function formatCountdown(seconds: number | null | undefined): string {
    if (seconds == null || seconds <= 0) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
}

export function formatBytes(bytes: number | null | undefined): string {
    if (bytes == null) return "—";
    const gb = bytes / 1_000_000_000;
    if (gb >= 10) return `${Math.round(gb)} GB`;
    if (gb >= 1) return `${gb.toFixed(1)} GB`;
    return `${Math.round(bytes / 1_000_000)} MB`;
}
